import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import { NotificationProvider } from './components/notifications/NotificationProvider';
import TopBar from './components/layout/TopBar';
import Sidebar from './components/layout/Sidebar';
import { Box } from '@mui/material';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Institutions from './pages/Institutions';
import InstitutionForm from './pages/InstitutionForm';
import Organizations from './pages/Organizations';
import OrganizationForm from './pages/OrganizationForm';
import Classes from './pages/Classes';
import ClassForm from './pages/ClassForm';
import Sections from './pages/Sections';
import SectionForm from './pages/SectionForm';
import Groups from './pages/Groups';
import GroupForm from './pages/GroupForm';
import Admissions from './pages/Admissions';
import AdmissionForm from './pages/AdmissionForm';
import ImportStudents from './pages/ImportStudents';
import Users from './pages/Users';
import UserForm from './pages/UserForm';
import FeeManagement from './pages/FeeManagement';
import Calendar from './pages/Calendar';
import Messages from './pages/Messages';
import Performance from './pages/Performance';
import StudentPromotion from './pages/StudentPromotion';
import Results from './pages/Results';
import ResultForm from './pages/ResultForm';
import BackupManagement from './pages/BackupManagement';
import RoleManagement from './pages/RoleManagement';
import BankAccounts from './pages/BankAccounts';
import ActivityLogs from './pages/ActivityLogs';
import FinanceDashboard from './pages/FinanceDashboard';
import usePermissions from './hooks/usePermissions';
import { PERMISSIONS } from './utils/constants';

// Clear dashboard filters from sessionStorage if the page is reloaded
try {
  const navigationEntry = performance.getEntriesByType('navigation')[0];
  if (navigationEntry && navigationEntry.type === 'reload') {
    sessionStorage.removeItem('dashboard_voucherFilter');
    sessionStorage.removeItem('finance_selectedCampus');
    sessionStorage.removeItem('finance_startDate');
    sessionStorage.removeItem('finance_endDate');
  }
} catch (e) {
  console.error('Failed to clear reload filters', e);
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});



// Protected Route Component with automatic TopBar and Sidebar
const ProtectedRoute = ({ children, title, permission }) => {
  const token = localStorage.getItem('token');
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(
    localStorage.getItem('sidebarCollapsed') === 'true'
  );

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };
  
  // Get page title from route or prop
  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    const titleMap = {
      '/dashboard': 'Dashboard',
      '/profile': 'Profile',
      '/organizations': 'Organizations',
      '/organizations/new': 'Create Organization',
      '/institutions': 'Institutions',
      '/institutions/new': 'Create Institution',
      '/classes': 'Classes',
      '/sections': 'Sections',
      '/groups': 'Groups',
      '/admissions': 'Admissions',
      '/users': 'Users',
      '/fee-management': 'Fee Management',
      '/calendar': 'Academic Calendar',
      '/messages': 'Messages',
      '/performance': 'Performance',
      '/student-promotion': 'Student Promotion',
      '/results': 'Results',
      '/backup-management': 'Backup Management',
      '/bank-accounts': 'Bank Accounts',
      '/activity-logs': 'Activity Logs',
      '/finance-dashboard': 'Finance Manager Dashboard',
    };
    
    // Check for edit routes
    if (path.includes('/edit/')) {
      if (path.includes('/organizations')) return 'Edit Organization';
      if (path.includes('/institutions')) return 'Edit Institution';
      if (path.includes('/classes')) return 'Edit Class';
      if (path.includes('/sections')) return 'Edit Section';
      if (path.includes('/groups')) return 'Edit Group';
      if (path.includes('/admissions')) return 'Edit Admission';
      if (path.includes('/users')) return 'Edit User';
      if (path.includes('/results')) return 'Edit Result';
    }
    
    // Check for new routes
    if (path.includes('/new')) {
      if (path.includes('/organizations')) return 'Create Organization';
      if (path.includes('/institutions')) return 'Create Institution';
      if (path.includes('/classes')) return 'Create Class';
      if (path.includes('/sections')) return 'Create Section';
      if (path.includes('/groups')) return 'Create Group';
      if (path.includes('/admissions')) return 'New Admission';
      if (path.includes('/users')) return 'Create User';
      if (path.includes('/results')) return 'Create Result';
    }
    
    return titleMap[path] || 'SGC Education';
  };
  
  if (!token) {
    return <Navigate to="/login" />;
  }

  // Check for granular permission
  if (permission && !hasPermission(permission)) {
    // If user lacks permission for dashboard itself, redirect to profile to prevent loop
    if (!hasPermission(PERMISSIONS.DASHBOARD.VIEW)) {
      return <Navigate to="/profile" />;
    }
    return <Navigate to="/dashboard" />; // Redirect to dashboard if unauthorized
  }

  const sidebarWidth = sidebarCollapsed ? 80 : 280;
  
  return (
    <Box sx={{ display: 'flex', width: '100%', minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden' }}>
      <TopBar title={getPageTitle()} onToggleSidebar={toggleSidebar} />
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // Crucial for Recharts responsive containers inside Flexbox
          width: { xs: '100%', sm: `calc(100% - ${sidebarWidth}px)` },
          maxWidth: { xs: '100%', sm: `calc(100% - ${sidebarWidth}px)` },
          mt: '64px',
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <NotificationProvider>
        <Router>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute permission={PERMISSIONS.DASHBOARD.VIEW}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance-dashboard"
            element={
              <ProtectedRoute permission={PERMISSIONS.FEES.VIEW}>
                <FinanceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations"
            element={
              <ProtectedRoute permission={PERMISSIONS.SYSTEM.MANAGE}>
                <Organizations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/new"
            element={
              <ProtectedRoute permission={PERMISSIONS.SYSTEM.MANAGE}>
                <OrganizationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/edit/:id"
            element={
              <ProtectedRoute permission={PERMISSIONS.SYSTEM.MANAGE}>
                <OrganizationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/institutions"
            element={
              <ProtectedRoute permission={PERMISSIONS.INSTITUTIONS.VIEW}>
                <Institutions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/institutions/new"
            element={
              <ProtectedRoute>
                <InstitutionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/institutions/edit/:id"
            element={
              <ProtectedRoute>
                <InstitutionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/classes"
            element={
              <ProtectedRoute permission={PERMISSIONS.ACADEMIC.MANAGE}>
                <Classes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/classes/new"
            element={
              <ProtectedRoute>
                <ClassForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/classes/edit/:id"
            element={
              <ProtectedRoute>
                <ClassForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sections"
            element={
              <ProtectedRoute>
                <Sections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sections/new"
            element={
              <ProtectedRoute>
                <SectionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sections/edit/:id"
            element={
              <ProtectedRoute>
                <SectionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups"
            element={
              <ProtectedRoute>
                <Groups />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/new"
            element={
              <ProtectedRoute>
                <GroupForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/edit/:id"
            element={
              <ProtectedRoute>
                <GroupForm />
              </ProtectedRoute>
            }
          />
          {/* Admissions Routes */}
          <Route
            path="/admissions"
            element={
              <ProtectedRoute permission={PERMISSIONS.ADMISSIONS.VIEW}>
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/reports"
            element={
              <ProtectedRoute>
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/register"
            element={
              <ProtectedRoute>
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/analytics"
            element={
              <ProtectedRoute>
                <Admissions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admissions/students/search-all"
            element={
              <ProtectedRoute>
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/students/search-family"
            element={
              <ProtectedRoute>
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/students/status"
            element={
              <ProtectedRoute>
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/students/import"
            element={
              <ProtectedRoute>
                <ImportStudents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/students/bulk-signup"
            element={
              <ProtectedRoute>
                <Admissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/new"
            element={
              <ProtectedRoute>
                <AdmissionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/edit/:id"
            element={
              <ProtectedRoute>
                <AdmissionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/view/:id"
            element={
              <ProtectedRoute>
                <AdmissionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute permission={PERMISSIONS.USERS.VIEW}>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/new"
            element={
              <ProtectedRoute>
                <UserForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/edit/:id"
            element={
              <ProtectedRoute>
                <UserForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/fee-management"
            element={
              <ProtectedRoute permission={PERMISSIONS.FEES.VIEW}>
                <FeeManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance"
            element={
              <ProtectedRoute>
                <Performance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-promotion"
            element={
              <ProtectedRoute>
                <StudentPromotion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results"
            element={
              <ProtectedRoute>
                <Results />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results/new"
            element={
              <ProtectedRoute>
                <ResultForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results/edit/:id"
            element={
              <ProtectedRoute>
                <ResultForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/backup-management"
            element={
              <ProtectedRoute permission={PERMISSIONS.SYSTEM.MANAGE}>
                <BackupManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <ProtectedRoute permission={PERMISSIONS.SYSTEM.MANAGE}>
                <RoleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bank-accounts"
            element={
              <ProtectedRoute permission={PERMISSIONS.SYSTEM.MANAGE}>
                <BankAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity-logs"
            element={
              <ProtectedRoute permission={PERMISSIONS.SYSTEM.MANAGE}>
                <ActivityLogs />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" />} />
        </Routes>
        </Router>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
